import * as m from '@staiter/i18n';
import type { MovePreviewReason, MovePreviewResponse } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  Plus,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { EmptyState, PageHeader, SkeletonTable } from '~/components/data';
import { useToast } from '~/components/feedback';
import { BulkImportWizard } from '~/components/imports';
import { Button, Checkbox, Dialog, Input } from '~/components/primitives';
import { ProvenanceChip } from '~/components/status';
import { fetchWarehouseUsage } from '~/lib/api/boms';
import { ApiRequestError } from '~/lib/api/client';
import { fetchWarehouses } from '~/lib/api/inventory';
import {
  createWarehouse,
  deleteWarehouse,
  moveWarehouse,
  previewWarehouseMove,
  updateWarehouse,
} from '~/lib/api/warehouses';
import shared from './_auth._shared.module.css';
import styles from './_auth.warehouses.module.css';

export const Route = createFileRoute('/_auth/warehouses')({ component: WarehousesTree });

type WarehouseNode = {
  id: string;
  code: string;
  name: string;
  parent_id: string | null;
  is_group: boolean;
  children: WarehouseNode[];
};

function reasonLabel(reason: MovePreviewReason): string {
  return m.route_warehouses_reason_label({ reason });
}

function WarehousesTree(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => fetchWarehouses(),
  });

  const [dragId, setDragId] = useState<string | null>(null);
  const [hoverTarget, setHoverTarget] = useState<string | null>(null);
  const [movePreview, setMovePreview] = useState<MovePreviewResponse | null>(null);
  const [confirmMove, setConfirmMove] = useState<{
    nodeId: string;
    targetId: string | null;
    preview: MovePreviewResponse;
  } | null>(null);
  const [addChildFor, setAddChildFor] = useState<string | null>(null);
  const [rename, setRename] = useState<{ id: string; name: string } | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  // FE-32: split-pane selectedNodeId для detail panel справа.
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  // FE-150 Sprint N+ batch 3: search filter для warehouse tree.
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ id: string; name: string } | null>(null);
  const [deleteUsage, setDeleteUsage] = useState<{
    loading: boolean;
    can_delete?: boolean;
    child_count?: number;
    sle_count?: number;
    stock_entry_count?: number;
  }>({ loading: false });
  const liveRef = useRef<HTMLDivElement>(null);

  const announce = (msg: string): void => {
    if (liveRef.current) {
      liveRef.current.textContent = msg;
    }
  };

  const tree = useMemo<WarehouseNode[]>(() => {
    const items = data?.items ?? [];
    const map = new Map<string, WarehouseNode>();
    items.forEach((w) =>
      map.set(w.id, {
        id: w.id,
        code: w.code,
        name: w.name,
        parent_id: w.parent_id,
        is_group: w.is_group ?? false,
        children: [],
      }),
    );
    const roots: WarehouseNode[] = [];
    map.forEach((node) => {
      if (node.parent_id && map.has(node.parent_id)) map.get(node.parent_id)!.children.push(node);
      else roots.push(node);
    });
    // FE-150 Sprint N+ batch 3: filter tree by search query (matches code OR name).
    // Preserves ancestor chains when descendants match — иначе tree станет flat.
    const q = searchQuery.trim().toLowerCase();
    if (!q) return roots;
    function matches(n: WarehouseNode): boolean {
      const self = n.code.toLowerCase().includes(q) || n.name.toLowerCase().includes(q);
      const child = n.children.some(matches);
      return self || child;
    }
    function prune(n: WarehouseNode): WarehouseNode {
      return { ...n, children: n.children.filter(matches).map(prune) };
    }
    return roots.filter(matches).map(prune);
  }, [data, searchQuery]);

  const previewMut = useMutation({
    mutationFn: ({ id, newParent }: { id: string; newParent: string | null }) =>
      previewWarehouseMove(id, newParent),
  });

  const moveMut = useMutation({
    mutationFn: ({ id, newParent }: { id: string; newParent: string | null }) =>
      moveWarehouse(id, { new_parent_id: newParent }),
    onSuccess: (_d, vars) => {
      announce(m.route_warehouses_announce_moved());
      void qc.invalidateQueries({ queryKey: ['warehouses'] });
      setConfirmMove(null);
      setDragId(null);
      setHoverTarget(null);
      setMovePreview(null);
      // track last move for undo
      void vars;
    },
  });

  const createMut = useMutation({
    mutationFn: (p: { code: string; name: string; parent_id: string | null; is_group: boolean }) =>
      createWarehouse(p),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouses'] });
      setAddChildFor(null);
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateWarehouse(id, { name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouses'] });
      setRename(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteWarehouse(id),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_warehouses_toast_deleted() });
      void qc.invalidateQueries({ queryKey: ['warehouses'] });
      setDeleteDialog(null);
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.status === 409) {
        toast.push({
          variant: 'error',
          title: m.route_warehouses_toast_delete_in_use_title(),
          description: m.route_warehouses_toast_delete_in_use_body(),
        });
      } else {
        toast.push({
          variant: 'error',
          title: m.route_warehouses_toast_delete_failed(),
          description:
            err instanceof Error ? err.message : m.route_warehouses_toast_unknown_error(),
        });
      }
      setDeleteDialog(null);
    },
  });

  const openDeleteDialog = async (id: string, name: string): Promise<void> => {
    setDeleteDialog({ id, name });
    setDeleteUsage({ loading: true });
    try {
      const usage = await fetchWarehouseUsage(id);
      setDeleteUsage({ loading: false, ...usage });
    } catch {
      setDeleteUsage({ loading: false, can_delete: false });
    }
  };

  const handleDragStart = (id: string): void => {
    setDragId(id);
    announce(m.route_warehouses_announce_move_start());
  };

  const handleDragOverNode = async (targetId: string | null): Promise<void> => {
    if (!dragId || targetId === dragId) return;
    setHoverTarget(targetId);
    try {
      const res = await previewMut.mutateAsync({ id: dragId, newParent: targetId });
      setMovePreview(res);
    } catch {
      setMovePreview({ ok: false, affected_descendants: 0, reason: 'self_parent' });
    }
  };

  const handleDrop = (targetId: string | null): void => {
    if (!dragId || !movePreview) return;
    if (!movePreview.ok) {
      announce(
        m.route_warehouses_announce_move_rejected({
          reason: movePreview.reason
            ? reasonLabel(movePreview.reason)
            : m.route_warehouses_reason_fallback(),
        }),
      );
      setDragId(null);
      setHoverTarget(null);
      setMovePreview(null);
      return;
    }
    if ((movePreview.affected_descendants ?? 0) > 50) {
      setConfirmMove({ nodeId: dragId, targetId, preview: movePreview });
      return;
    }
    moveMut.mutate({ id: dragId, newParent: targetId });
  };

  const handleKeyMove = (nodeId: string, direction: 'up' | 'down' | 'out' | 'in'): void => {
    // Smart keyboard reparent: 'out' → to parent's parent; 'in' → to previous sibling.
    const items = data?.items ?? [];
    const current = items.find((w) => w.id === nodeId);
    if (!current) return;
    let newParent: string | null = current.parent_id;
    if (direction === 'out') {
      const parent = items.find((w) => w.id === current.parent_id);
      newParent = parent?.parent_id ?? null;
    } else if (direction === 'in') {
      const siblings = items.filter((w) => w.parent_id === current.parent_id && w.id !== nodeId);
      newParent = siblings[0]?.id ?? null;
    }
    if (direction === 'up' || direction === 'down') {
      announce(m.route_warehouses_announce_reorder_todo());
      return;
    }
    if (newParent === current.parent_id) return;
    void (async () => {
      const res = await previewWarehouseMove(nodeId, newParent);
      if (!res.ok) {
        announce(
          m.route_warehouses_announce_move_impossible({
            reason: res.reason ? reasonLabel(res.reason) : m.route_warehouses_reason_fallback(),
          }),
        );
        return;
      }
      moveMut.mutate({ id: nodeId, newParent });
    })();
  };

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_warehouses()}
        description={m.route_warehouses_description()}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Upload size={14} />}
              onClick={() => setImportOpen(true)}
            >
              {m.route_warehouses_btn_bulk_import()}
            </Button>
            <Button leadingIcon={<Plus size={14} />} onClick={() => setAddChildFor('')}>
              {m.route_warehouses_btn_new()}
            </Button>
          </>
        }
      />

      <div ref={liveRef} role="status" aria-live="polite" className={shared.srOnly} />

      {/* FE-150 Sprint N+ batch 3: search filter for warehouse tree. */}
      <div className={styles.searchRow}>
        <Input
          label={m.warehouses_search_placeholder()}
          hideLabel
          placeholder={m.warehouses_search_placeholder()}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leadingIcon={<Search size={14} />}
        />
      </div>

      {isLoading ? (
        <SkeletonTable rows={6} cols={3} />
      ) : tree.length === 0 ? (
        <EmptyState
          title={m.route_warehouses_empty_title()}
          description={m.route_warehouses_empty_description()}
          action={
            <Button variant="primary" onClick={() => setImportOpen(true)}>
              {m.route_warehouses_btn_bulk_import()}
            </Button>
          }
        />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '60fr 40fr',
            gap: 'var(--space-4)',
            alignItems: 'start',
          }}
        >
          <ul
            className={styles.tree}
            onDragOver={(e) => {
              e.preventDefault();
              void handleDragOverNode(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(null);
            }}
          >
            {tree.map((n) => (
              <TreeNode
                key={n.id}
                node={n}
                depth={0}
                dragId={dragId}
                hoverTarget={hoverTarget}
                movePreview={movePreview}
                focusedId={focusedId}
                onFocus={(id) => {
                  setFocusedId(id);
                  setSelectedNodeId(id);
                }}
                onDragStart={handleDragStart}
                onDragEnd={() => {
                  setDragId(null);
                  setHoverTarget(null);
                  setMovePreview(null);
                }}
                onDragOverNode={handleDragOverNode}
                onDrop={handleDrop}
                onAddChild={(id) => setAddChildFor(id)}
                onRename={(id, name) => setRename({ id, name })}
                onDelete={(id) => {
                  const node = (data?.items ?? []).find((w) => w.id === id);
                  void openDeleteDialog(id, node?.name ?? id.slice(0, 8));
                }}
                onKeyMove={handleKeyMove}
              />
            ))}
          </ul>
          {/* FE-32: right-side detail panel. */}
          <aside
            style={{
              padding: 'var(--space-4)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-bg-surface)',
              position: 'sticky',
              top: 'var(--space-3)',
            }}
            aria-label="Детали склада"
          >
            {(() => {
              const node = (data?.items ?? []).find((w) => w.id === selectedNodeId);
              if (!node) {
                return (
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
                    {m.ui_vyberite_uzel_dereva_sleva_dlya_prosmotra_detaley()}
                  </p>
                );
              }
              return (
                <div>
                  <h3 style={{ marginTop: 0, marginBottom: 8 }}>{node.name}</h3>
                  <div
                    style={{
                      marginBottom: 12,
                      color: 'var(--color-text-secondary)',
                      fontSize: 13,
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {node.code}
                  </div>
                  <dl
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr',
                      gap: '8px 12px',
                      fontSize: 13,
                    }}
                  >
                    <dt style={{ color: 'var(--color-text-secondary)' }}>{m.ui_tip()}</dt>
                    <dd style={{ margin: 0 }}>{node.is_group ? 'Группа' : 'Склад'}</dd>
                    {(node as { source_system?: string }).source_system && (
                      <>
                        <dt style={{ color: 'var(--color-text-secondary)' }}>{m.ui_istochnik()}</dt>
                        <dd style={{ margin: 0 }}>
                          <ProvenanceChip
                            source={(node as unknown as { source_system: string }).source_system}
                            {...((node as unknown as { fetched_at?: string }).fetched_at
                              ? {
                                  fetched_at: (node as unknown as { fetched_at: string })
                                    .fetched_at,
                                }
                              : {})}
                          />
                        </dd>
                      </>
                    )}
                    {(node as { address?: string }).address && (
                      <>
                        <dt style={{ color: 'var(--color-text-secondary)' }}>{m.ui_adres()}</dt>
                        <dd style={{ margin: 0 }}>{(node as { address: string }).address}</dd>
                      </>
                    )}
                    <dt style={{ color: 'var(--color-text-secondary)' }}>Parent ID</dt>
                    <dd style={{ margin: 0, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                      {node.parent_id ?? '—'}
                    </dd>
                  </dl>
                  <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                    <Button size="sm" variant="secondary" onClick={() => setAddChildFor(node.id)}>
                      {m.ui_docherniy()}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setRename({ id: node.id, name: node.name })}
                    >
                      {m.ui_pereimenovat()}
                    </Button>
                  </div>
                </div>
              );
            })()}
          </aside>
        </div>
      )}

      {confirmMove && (
        <Dialog
          open
          onClose={() => setConfirmMove(null)}
          title={m.route_warehouses_confirm_move_title()}
          footer={
            <div className={shared.rowEnd}>
              <Button variant="secondary" onClick={() => setConfirmMove(null)}>
                {m.common_button_cancel()}
              </Button>
              <Button
                variant="primary"
                onClick={() =>
                  moveMut.mutate({ id: confirmMove.nodeId, newParent: confirmMove.targetId })
                }
                disabled={moveMut.isPending}
              >
                {m.route_warehouses_confirm_move_btn()}
              </Button>
            </div>
          }
        >
          <p>
            {m.route_warehouses_confirm_affected()}{' '}
            <strong>{confirmMove.preview.affected_descendants}</strong>
          </p>
          {confirmMove.preview.new_path && (
            <p className={shared.mono}>
              {m.route_warehouses_confirm_new_path({ path: confirmMove.preview.new_path })}
            </p>
          )}
        </Dialog>
      )}

      {addChildFor !== null && (
        <AddChildDialog
          parentId={addChildFor || null}
          onClose={() => setAddChildFor(null)}
          onSubmit={(p) => createMut.mutate(p)}
          pending={createMut.isPending}
        />
      )}

      {rename && (
        <RenameDialog
          initial={rename.name}
          onClose={() => setRename(null)}
          onSubmit={(name) => updateMut.mutate({ id: rename.id, name })}
          pending={updateMut.isPending}
        />
      )}

      <BulkImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        defaultResource="warehouses"
      />

      {deleteDialog && (
        <Dialog
          open
          onClose={() => setDeleteDialog(null)}
          title={m.route_warehouses_delete_title()}
          size="md"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDeleteDialog(null)}>
                {m.common_button_cancel()}
              </Button>
              <Button
                variant="destructive"
                disabled={deleteUsage.loading || !deleteUsage.can_delete || deleteMut.isPending}
                loading={deleteMut.isPending}
                onClick={() => deleteMut.mutate(deleteDialog.id)}
              >
                {m.common_button_delete()}
              </Button>
            </>
          }
        >
          {deleteUsage.loading ? (
            <p>{m.route_warehouses_delete_checking()}</p>
          ) : (
            <div className={styles.deleteBody}>
              {!deleteUsage.can_delete && (
                <div className={[shared.inlineAlert, shared.inlineAlertDanger].join(' ')}>
                  {(deleteUsage.child_count ?? 0) > 0
                    ? m.route_warehouses_delete_has_children({
                        count: deleteUsage.child_count ?? 0,
                      })
                    : (deleteUsage.sle_count ?? 0) > 0 || (deleteUsage.stock_entry_count ?? 0) > 0
                      ? m.route_warehouses_delete_in_use()
                      : m.route_warehouses_delete_impossible()}
                </div>
              )}
              {deleteUsage.can_delete && (
                <p>
                  {m.route_warehouses_delete_confirm_prefix()} <strong>{deleteDialog.name}</strong>
                  {m.route_warehouses_delete_confirm_suffix()}
                </p>
              )}
              <dl className={styles.usageGrid}>
                <dt>{m.route_warehouses_delete_child_count()}</dt>
                <dd>{deleteUsage.child_count ?? 0}</dd>
                <dt>{m.route_warehouses_delete_sle_count()}</dt>
                <dd>{deleteUsage.sle_count ?? 0}</dd>
                <dt>{m.route_warehouses_delete_se_count()}</dt>
                <dd>{deleteUsage.stock_entry_count ?? 0}</dd>
              </dl>
            </div>
          )}
        </Dialog>
      )}
    </div>
  );
}

type TreeNodeProps = {
  node: WarehouseNode;
  depth: number;
  dragId: string | null;
  hoverTarget: string | null;
  movePreview: MovePreviewResponse | null;
  focusedId: string | null;
  onFocus: (id: string) => void;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDragOverNode: (id: string | null) => Promise<void>;
  onDrop: (id: string | null) => void;
  onAddChild: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onKeyMove: (id: string, dir: 'up' | 'down' | 'out' | 'in') => void;
};

function TreeNode(props: TreeNodeProps): JSX.Element {
  const {
    node,
    depth,
    dragId,
    hoverTarget,
    movePreview,
    focusedId,
    onFocus,
    onDragStart,
    onDragEnd,
    onDragOverNode,
    onDrop,
    onAddChild,
    onRename,
    onDelete,
    onKeyMove,
  } = props;
  const [open, setOpen] = useState(true);
  const hasChildren = node.children.length > 0;
  const isHoverTarget = hoverTarget === node.id && dragId && dragId !== node.id;
  const dropOk = isHoverTarget && movePreview?.ok;
  const dropBad = isHoverTarget && movePreview && !movePreview.ok;
  const isFocused = focusedId === node.id;

  const reasonTip = dropBad && movePreview?.reason ? reasonLabel(movePreview.reason) : undefined;

  return (
    <li>
      <div
        role="treeitem"
        tabIndex={isFocused ? 0 : -1}
        aria-level={depth + 1}
        aria-expanded={hasChildren ? open : undefined}
        draggable
        onFocus={() => onFocus(node.id)}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          onDragStart(node.id);
        }}
        onDragEnd={onDragEnd}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void onDragOverNode(node.id);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDrop(node.id);
        }}
        onKeyDown={(e) => {
          // Smart keyboard move: Cmd/Ctrl+Shift+Arrow
          if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
            const map: Record<string, 'up' | 'down' | 'out' | 'in'> = {
              ArrowUp: 'up',
              ArrowDown: 'down',
              ArrowLeft: 'out',
              ArrowRight: 'in',
            };
            const dir = map[e.key];
            if (dir) {
              e.preventDefault();
              onKeyMove(node.id, dir);
            }
          }
          if (e.key === ' ') {
            // Space = toggle drag mode (announce only, not native)
            e.preventDefault();
          }
        }}
        title={reasonTip}
        className={[
          styles.treeNode,
          dropOk ? styles.treeNodeDropOk : '',
          dropBad ? styles.treeNodeDropBad : '',
          dragId === node.id ? styles.treeNodeDragging : '',
          isFocused ? styles.treeNodeFocused : '',
        ]
          .filter(Boolean)
          .join(' ')}
        // staiter-inline-style-ok: CSS custom prop --depth для recursive tree indent
        style={{ '--depth': depth } as React.CSSProperties}
      >
        <GripVertical size={12} aria-hidden="true" className={styles.grip} />
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-label={
              open ? m.route_warehouses_aria_collapse() : m.route_warehouses_aria_expand()
            }
            className={styles.chevronBtn}
          >
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : (
          <span className={styles.chevronSpacer} />
        )}
        <span className={shared.mono}>{node.code}</span>
        <span>{node.name}</span>
        {node.is_group && (
          <span className={shared.subtle}>{m.route_warehouses_node_is_group()}</span>
        )}
        {/* FE-33: ProvenanceChip on 1С-source warehouses. */}
        {(node as unknown as { source_system?: string }).source_system &&
          (node as unknown as { source_system?: string }).source_system !== 'staiter' && (
            <ProvenanceChip
              source={(node as unknown as { source_system: string }).source_system}
              {...((node as unknown as { fetched_at?: string }).fetched_at
                ? {
                    fetched_at: (node as unknown as { fetched_at: string }).fetched_at,
                  }
                : {})}
            />
          )}
        {isHoverTarget && movePreview?.ok && (movePreview.affected_descendants ?? 0) > 0 && (
          <span className={[shared.subtle, styles.hoverChip].join(' ')}>
            {m.route_warehouses_hover_affected({
              count: movePreview.affected_descendants ?? 0,
            })}
          </span>
        )}
        {isHoverTarget && dropBad && reasonTip && (
          <span className={styles.reasonTip}>{reasonTip}</span>
        )}
        <div className={styles.nodeActions}>
          <Button size="sm" variant="ghost" onClick={() => onAddChild(node.id)}>
            {m.route_warehouses_node_btn_add_child()}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onRename(node.id, node.name)}>
            {m.route_warehouses_node_btn_rename()}
          </Button>
          {/* FE-149 Sprint N+ batch 3: destructive action visual weight (icon + danger color). */}
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<Trash2 size={14} />}
            className={styles.deleteBtn}
            aria-label={m.warehouses_btn_delete_aria()}
            onClick={() => onDelete(node.id)}
          >
            {m.common_button_delete()}
          </Button>
        </div>
      </div>
      {open && hasChildren && (
        <ul className={styles.treeGroup} role="group">
          {node.children.map((c) => (
            <TreeNode key={c.id} {...props} node={c} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

function AddChildDialog({
  parentId,
  onClose,
  onSubmit,
  pending,
}: {
  parentId: string | null;
  onClose: () => void;
  onSubmit: (p: {
    code: string;
    name: string;
    parent_id: string | null;
    is_group: boolean;
  }) => void;
  pending: boolean;
}): JSX.Element {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [isGroup, setIsGroup] = useState(false);
  return (
    <Dialog
      open
      onClose={onClose}
      title={parentId ? m.route_warehouses_add_child_title() : m.route_warehouses_add_root_title()}
      footer={
        <div className={shared.rowEnd}>
          <Button variant="secondary" onClick={onClose}>
            {m.common_button_cancel()}
          </Button>
          <Button
            variant="primary"
            disabled={!code || !name || pending}
            onClick={() => onSubmit({ code, name, parent_id: parentId, is_group: isGroup })}
          >
            {m.common_button_create()}
          </Button>
        </div>
      }
    >
      <div className={shared.stack3}>
        <Input
          label={m.route_warehouses_field_code()}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Input
          label={m.route_warehouses_field_name()}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Checkbox
          label={m.route_warehouses_field_is_group()}
          checked={isGroup}
          onChange={(e) => setIsGroup(e.target.checked)}
        />
      </div>
    </Dialog>
  );
}

function RenameDialog({
  initial,
  onClose,
  onSubmit,
  pending,
}: {
  initial: string;
  onClose: () => void;
  onSubmit: (name: string) => void;
  pending: boolean;
}): JSX.Element {
  const [name, setName] = useState(initial);
  return (
    <Dialog
      open
      onClose={onClose}
      title={m.route_warehouses_rename_title()}
      footer={
        <div className={shared.rowEnd}>
          <Button variant="secondary" onClick={onClose}>
            {m.common_button_cancel()}
          </Button>
          <Button variant="primary" disabled={!name || pending} onClick={() => onSubmit(name)}>
            {m.common_button_save()}
          </Button>
        </div>
      }
    >
      <Input
        label={m.route_warehouses_field_name()}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
    </Dialog>
  );
}
